import { DOCUMENT } from '@angular/common';
import { DestroyRef, Inject, Injectable } from '@angular/core';
import { BaseServiceResponse } from '../../models/api-calls/base-service-response';
import { ServiceResponse } from '../../models/api-calls/service-response';
import { AccountDto, accountForSession } from '../../models/dtos/account-dto';
import { SignInWithEmailRequest } from '../../models/requests/sign-in/sign-in-with-email-request';
import { SignUpWithEmailRequest } from '../../models/requests/sign-in/sign-up-with-email-request';
import { ResetPasswordWithTokenRequest } from '../../models/requests/auth/reset-password-with-token-request';
import { AuthTokenResponse } from '../../models/responses/auth/auth-token-response';
import { ApiWrapperService } from '../api-wrapper-service/api-wrapper-services';
import { SessionService } from './session.service';

@Injectable({ providedIn: 'root' })
export class AccountService {
  // Private variables

  private initialised = false;
  private authAttempt = 0;
  private restoration?: { version: number; promise: Promise<boolean> };

  // Public properties

  // Constructors

  constructor(
    private readonly api: ApiWrapperService,
    private readonly session: SessionService,
    @Inject(DOCUMENT) private readonly document: Document,
    destroyRef: DestroyRef
  ) {
    const onStorage = (event: StorageEvent): void => this.storageChanged(event);
    this.document.defaultView?.addEventListener('storage', onStorage);
    destroyRef.onDestroy(() => this.document.defaultView?.removeEventListener('storage', onStorage));
  }

  // Public methods

  public restoreSession(): Promise<boolean> {
    if (this.session.account()) {
      return Promise.resolve(true);
    }

    if (!this.initialised) {
      this.initialised = true;
      const stored = this.session.readStoredToken();
      if (stored) {
        this.session.begin(stored, false);
      }
    }

    const token = this.session.currentToken;
    const version = this.session.version;
    if (!token) {
      return Promise.resolve(false);
    }
    if (this.restoration?.version === version) {
      return this.restoration.promise;
    }

    const promise = this.loadAccount(token, version).then(result => result.succeeded && !!result.payload);
    this.restoration = { version, promise };
    void promise.finally(() => {
      if (this.restoration?.promise === promise) {
        this.restoration = undefined;
      }
    });
    return promise;
  }

  public signInWithEmail(request: SignInWithEmailRequest): Promise<BaseServiceResponse<AccountDto>> {
    return this.authenticate(() => this.api.signInWithEmail(request));
  }

  public signUpWithEmail(request: SignUpWithEmailRequest): Promise<BaseServiceResponse<AccountDto>> {
    return this.authenticate(() => this.api.signUpWithEmail(request));
  }

  public resetPassword(request: ResetPasswordWithTokenRequest): Promise<BaseServiceResponse<AccountDto>> {
    return this.authenticate(() => this.api.resetPassword(request));
  }

  public loginWithToken(token: string): Promise<BaseServiceResponse<AccountDto>> {
    return this.authenticate(() => this.api.loginWithToken({ token }));
  }

  public async requestResetPassword(email: string): Promise<BaseServiceResponse<void>> {
    return BaseServiceResponse.fromServiceResponse(await this.api.requestResetPassword({ email }));
  }

  public async requestLoginToken(email: string): Promise<BaseServiceResponse<void>> {
    return BaseServiceResponse.fromServiceResponse(await this.api.requestLoginToken({ email }));
  }

  public async rejectResetPassword(token: string): Promise<BaseServiceResponse<void>> {
    return BaseServiceResponse.fromServiceResponse(await this.api.rejectResetPassword({ token }));
  }

  public async rejectLoginWithToken(token: string): Promise<BaseServiceResponse<void>> {
    return BaseServiceResponse.fromServiceResponse(await this.api.rejectLoginWithToken({ token }));
  }

  public signOut(message = ''): void {
    this.initialised = true;
    this.authAttempt++;
    this.session.clear(message);
  }

  // Private methods

  private async authenticate(
    operation: () => Promise<ServiceResponse<AuthTokenResponse>>
  ): Promise<BaseServiceResponse<AccountDto>> {
    const version = this.session.version;
    const attempt = ++this.authAttempt;
    const response = BaseServiceResponse.fromServiceResponse(await operation());

    if (this.session.version !== version || this.authAttempt !== attempt) {
      return this.changedSession();
    }
    if (!response.succeeded) {
      return BaseServiceResponse.mapPayload<AuthTokenResponse, AccountDto>(response, () => undefined);
    }

    const token = response.payload?.accessToken;
    if (typeof token !== 'string' || !token.trim()) {
      return BaseServiceResponse.fromServiceResponse({ status: 500, error: {
        message: 'We couldn’t confirm your sign-in. Try signing in with your password rather than creating another account or resetting it again.'
      } });
    }

    this.initialised = true;
    const sessionVersion = this.session.begin(token);
    return this.loadAccount(token, sessionVersion);
  }

  private async loadAccount(token: string, version: number): Promise<BaseServiceResponse<AccountDto>> {
    try {
      const result = BaseServiceResponse.fromServiceResponse(await this.api.getCurrentAccount());
      if (!this.session.isCurrent(token, version)) {
        return this.changedSession();
      }
      if (result.status === 401) {
        this.session.expire(token, version);
        return result;
      }

      const source = result.payload;
      if (result.succeeded && source && typeof source.id === 'string' && source.id && typeof source.name === 'string') {
        const account = accountForSession(source);
        this.session.commit(token, version, account);
        return BaseServiceResponse.mapPayload(result, () => account);
      }
    } catch {
      // Keep the token: loss of connectivity is not evidence of revoked access.
    }

    this.session.unavailable(token, version);
    return BaseServiceResponse.fromServiceResponse({ status: 503, error: {
      message: 'We couldn’t load your account. Choose Reconnect to try again; you don’t need to use your sign-in link again.'
    } });
  }

  private changedSession(): BaseServiceResponse<AccountDto> {
    return BaseServiceResponse.fromServiceResponse({ status: 401, error: {
      message: 'You’re now signed in with a different account. Continue with that account.'
    } });
  }

  private storageChanged(event: StorageEvent): void {
    if (event.key !== this.session.storageKey && event.key !== null) {
      return;
    }
    try {
      if (event.storageArea !== this.document.defaultView?.localStorage) {
        return;
      }
    } catch {
      return;
    }

    const token = this.session.readStoredToken();
    if (token === this.session.currentToken) {
      return;
    }

    this.initialised = true;
    this.authAttempt++;
    if (token) {
      this.session.begin(token, false);
      void this.restoreSession();
    } else {
      this.session.clear('You signed out in another tab. Please sign in again to continue.', false);
      this.session.invalidated.next();
    }
  }
}
